import { fetchCamera, place360UsesLiveCameraList } from "./cameras.ts";
import {
  isBuggyPanSpanTiltFocus,
  normYForBuggyPanSpanTiltFocus,
  ringNormToPtz,
} from "../lib/ptzPanoramaMap.ts";
import { apiGet, apiSend } from "./client.ts";

export type PtzState = {
  pan: number;
  tilt: number;
  zoom: number;
  hardwareWarning?: string;
};

export type PtzLimits = {
  pan: { min: number; max: number };
  tilt: { min: number; max: number };
  zoom: { min: number; max: number };
  panOffsetDeg: number;
  tiltOffsetDeg: number;
};

export type PtzRelativeBody = {
  dPan?: number;
  dTilt?: number;
  dZoom?: number;
};

export type PtzContinuousBody = {
  pan?: number;
  tilt?: number;
  zoom?: number;
  timeoutMs?: number;
};

export type PtzStopBody = {
  panTilt?: boolean;
  zoom?: boolean;
};

const sim = new Map<string, PtzState>();

function defaultState(): PtzState {
  return { pan: 0, tilt: 15, zoom: 5 };
}

function getSim(id: string): PtzState {
  const cur = sim.get(id);
  if (cur) return { ...cur };
  const s = defaultState();
  sim.set(id, s);
  return { ...s };
}

function wrapDeg(v: number): number {
  let x = v % 360;
  if (x < -180) x += 360;
  if (x > 180) x -= 360;
  return x;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

function unwrapEntityPayload(body: unknown): Record<string, unknown> | null {
  if (!body || typeof body !== "object") return null;
  const o = body as Record<string, unknown>;
  const inner = o.data;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    return inner as Record<string, unknown>;
  }
  return o;
}

function pickFiniteNum(v: unknown, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function cameraPtzPath(cameraId: string, segment: string): string {
  return `/api/cameras/${encodeURIComponent(cameraId)}/ptz/${segment}`;
}

function mapPtzStateRow(row: Record<string, unknown>): PtzState {
  const state: PtzState = {
    pan: pickFiniteNum(row.pan, 0),
    tilt: pickFiniteNum(row.tilt, 0),
    zoom: pickFiniteNum(row.zoom, 1),
  };
  const warn =
    (typeof row.hardwareWarning === "string" && row.hardwareWarning) ||
    (typeof row.hardware_warning === "string" && row.hardware_warning) ||
    "";
  if (warn.trim()) state.hardwareWarning = warn.trim();
  return state;
}

function mapAxisRange(
  row: Record<string, unknown>,
  axis: string,
  fallback: { min: number; max: number },
): { min: number; max: number } {
  const nested = row[axis];
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    const o = nested as Record<string, unknown>;
    return {
      min: pickFiniteNum(o.min, fallback.min),
      max: pickFiniteNum(o.max, fallback.max),
    };
  }
  return {
    min: pickFiniteNum(row[`${axis}_min`] ?? row[`${axis}Min`], fallback.min),
    max: pickFiniteNum(row[`${axis}_max`] ?? row[`${axis}Max`], fallback.max),
  };
}

function mapPtzLimitsRow(row: Record<string, unknown>): PtzLimits {
  return {
    pan: mapAxisRange(row, "pan", { min: -180, max: 180 }),
    tilt: mapAxisRange(row, "tilt", { min: -15, max: 90 }),
    zoom: mapAxisRange(row, "zoom", { min: 1, max: 25 }),
    panOffsetDeg: pickFiniteNum(row.panOffsetDeg ?? row.pan_offset_deg, 0),
    tiltOffsetDeg: pickFiniteNum(row.tiltOffsetDeg ?? row.tilt_offset_deg, 0),
  };
}

async function mockPtzLimits(cameraId: string): Promise<PtzLimits> {
  const cam = await fetchCamera(cameraId);
  const zMin = cam.zoomMin ?? 1;
  const zMax = cam.zoomMax ?? 25;
  const panOff = cam.panOffsetDeg ?? 0;
  const tiltOff = cam.tiltOffsetDeg ?? 0;
  return {
    pan: { min: -180, max: 180 },
    tilt: { min: -15, max: 90 },
    zoom: { min: zMin, max: zMax },
    panOffsetDeg: panOff,
    tiltOffsetDeg: tiltOff,
  };
}

/** GET /api/cameras/:id/ptz/limits */
export async function fetchPtzLimits(cameraId: string): Promise<PtzLimits> {
  if (place360UsesLiveCameraList()) {
    const body = await apiGet<unknown>(cameraPtzPath(cameraId, "limits"));
    const row = unwrapEntityPayload(body);
    if (!row) throw new Error("Invalid PTZ limits response");
    return mapPtzLimitsRow(row);
  }
  return mockPtzLimits(cameraId);
}

/** GET /api/cameras/:id/ptz/state */
export async function fetchPtzState(cameraId: string): Promise<PtzState> {
  if (place360UsesLiveCameraList()) {
    const body = await apiGet<unknown>(cameraPtzPath(cameraId, "state"));
    const row = unwrapEntityPayload(body);
    if (!row) throw new Error("Invalid PTZ state response");
    return mapPtzStateRow(row);
  }
  return getSim(cameraId);
}

/** POST /api/cameras/:id/ptz/continuous */
export async function ptzContinuousMove(
  cameraId: string,
  body: PtzContinuousBody,
): Promise<PtzState> {
  if (place360UsesLiveCameraList()) {
    const res = await apiSend<unknown>(
      cameraPtzPath(cameraId, "continuous"),
      "POST",
      body,
    );
    const row = unwrapEntityPayload(res);
    if (!row) throw new Error("Invalid PTZ continuous response");
    const next = mapPtzStateRow(row);
    sim.set(cameraId, next);
    return next;
  }
  return getSim(cameraId);
}

/** POST /api/cameras/:id/ptz/reset-zoom — continuous zoom-out (~10s) then sync. */
export async function ptzResetZoom(
  cameraId: string,
  body: { durationMs?: number } = {},
): Promise<PtzState> {
  if (place360UsesLiveCameraList()) {
    const res = await apiSend<unknown>(
      cameraPtzPath(cameraId, "reset-zoom"),
      "POST",
      body,
    );
    const row = unwrapEntityPayload(res);
    if (!row) throw new Error("Invalid PTZ reset-zoom response");
    const next = mapPtzStateRow(row);
    sim.set(cameraId, next);
    return next;
  }
  const limits = await mockPtzLimits(cameraId);
  const next: PtzState = {
    ...getSim(cameraId),
    zoom: limits.zoom.min,
  };
  sim.set(cameraId, next);
  return next;
}

/** POST /api/cameras/:id/ptz/stop */
export async function ptzStop(
  cameraId: string,
  body: PtzStopBody = {},
): Promise<PtzState> {
  if (place360UsesLiveCameraList()) {
    const res = await apiSend<unknown>(
      cameraPtzPath(cameraId, "stop"),
      "POST",
      body,
    );
    const row = unwrapEntityPayload(res);
    if (!row) throw new Error("Invalid PTZ stop response");
    const next = mapPtzStateRow(row);
    sim.set(cameraId, next);
    return next;
  }
  return getSim(cameraId);
}

/** POST /api/cameras/:id/ptz/relative (pulse: continuous + stop) */
export async function ptzRelativeMove(
  cameraId: string,
  body: PtzRelativeBody,
): Promise<PtzState> {
  if (place360UsesLiveCameraList()) {
    const res = await apiSend<unknown>(
      cameraPtzPath(cameraId, "relative"),
      "POST",
      body,
    );
    const row = unwrapEntityPayload(res);
    if (!row) throw new Error("Invalid PTZ relative response");
    const next = mapPtzStateRow(row);
    sim.set(cameraId, next);
    return next;
  }

  const limits = await mockPtzLimits(cameraId);
  const cur = getSim(cameraId);
  const panEndless = Math.abs(limits.pan.max - limits.pan.min - 360) < 1e-3;
  let pan = cur.pan + (body.dPan ?? 0);
  if (panEndless) {
    pan = wrapDeg(pan);
  } else {
    pan = clamp(pan, limits.pan.min, limits.pan.max);
  }
  const tilt = clamp(
    cur.tilt + (body.dTilt ?? 0),
    limits.tilt.min,
    limits.tilt.max,
  );
  const zoom = clamp(
    cur.zoom + (body.dZoom ?? 0),
    limits.zoom.min,
    limits.zoom.max,
  );
  const next: PtzState = { pan, tilt, zoom };
  sim.set(cameraId, next);
  return next;
}

export type PtzAbsoluteBody = {
  pan: number;
  tilt: number;
  zoom: number;
};

/** POST /api/cameras/:id/ptz/absolute — ONVIF AbsoluteMove (Hikvision pad / zoom). */
export async function ptzAbsoluteMove(
  cameraId: string,
  body: PtzAbsoluteBody,
): Promise<PtzState> {
  if (place360UsesLiveCameraList()) {
    const res = await apiSend<unknown>(
      cameraPtzPath(cameraId, "absolute"),
      "POST",
      body,
    );
    const row = unwrapEntityPayload(res);
    if (!row) throw new Error("Invalid PTZ absolute response");
    const next = mapPtzStateRow(row);
    sim.set(cameraId, next);
    return next;
  }

  const limits = await mockPtzLimits(cameraId);
  const pan = Math.min(
    limits.pan.max,
    Math.max(limits.pan.min, Number(body.pan)),
  );
  const tilt = Math.min(
    limits.tilt.max,
    Math.max(limits.tilt.min, Number(body.tilt)),
  );
  const zoom = Math.round(
    Math.min(
      limits.zoom.max,
      Math.max(limits.zoom.min, Number(body.zoom)),
    ),
  );
  const next: PtzState = { pan, tilt, zoom };
  sim.set(cameraId, next);
  return next;
}

/** POST /api/cameras/:id/ptz/focus */
export async function ptzFocusFromRing(
  cameraId: string,
  body: { normX: number; normY: number; zoom: number },
): Promise<PtzState> {
  if (place360UsesLiveCameraList()) {
    const limits = await fetchPtzLimits(cameraId);
    const target = ringNormToPtz(body.normX, body.normY, body.zoom, limits);

    const postFocus = async (payload: { normX: number; normY: number; zoom: number }) => {
      const res = await apiSend<unknown>(
        cameraPtzPath(cameraId, "focus"),
        "POST",
        payload,
      );
      const row = unwrapEntityPayload(res);
      if (!row) throw new Error("Invalid PTZ focus response");
      return mapPtzStateRow(row);
    };

    let next = await postFocus(body);
    if (isBuggyPanSpanTiltFocus(body.normY, next.tilt, limits)) {
      const correctedNormY = normYForBuggyPanSpanTiltFocus(target.tilt, limits);
      if (Math.abs(correctedNormY - body.normY) > 0.02) {
        next = await postFocus({
          normX: body.normX,
          normY: correctedNormY,
          zoom: body.zoom,
        });
      }
    }

    sim.set(cameraId, next);
    return next;
  }

  const limits = await mockPtzLimits(cameraId);
  const next: PtzState = ringNormToPtz(
    body.normX,
    body.normY,
    body.zoom,
    limits,
  );
  sim.set(cameraId, next);
  return next;
}
